import clsx from 'clsx';
import { SkipToContent } from './components/skip-to-content';
import { useAuthQuery } from '@/services/auth';
import { useListWorkspaces, useSampleWorkspace } from '@/features/workspaces';
import {
  announceCallbackComplete,
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
  subscribeToRefreshAuth,
  subscribeToRefreshWorkspaces,
} from '@customerio/hydra-nav-state';
import { PermissionsProvider } from '@customerio/hydra-permissions/react';
import { type RoleDefinition } from '@customerio/hydra-permissions/roles';
import { isStandardRole, useRoleDefinitions } from '@/features/roles';
import { Suspense, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, useLocation } from '@/services/router/compat';
import { appendMasqParam } from '@/utils/masq';
import { MainNavigation } from './components/main-navigation';
import { MainNavigationWrapper, NAV_COLLAPSED_LOCAL_STORAGE_KEY } from './components/main-navigation/wrapper';
import { isEmptyString } from '@/utils/string';
import { RecordPerformanceMetric } from './components/record-performance-metric';
import { useActiveBundle } from '@/context/active-bundle';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '@/context/current-workspace';
import { PerformanceMetric, PerformanceProvider } from '@/context/performance';
import { SidebarRoot } from '@/features/sidebar';
import { HelpSidebar } from '@/features/help-sidebar';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { appContainerFadedOutStore, appContainerOutletRenderedStore } from '@/stores/root';
import { analytics } from '@/services/analytics/client';
import { useAnalytics } from '@/services/analytics';
import { useIsSettings } from '@/services/router/use-is-settings';
import { AppBundle } from '@/utils/app-bundles';
import { VideoLibrary, VideoLibraryProvider } from '@/features/video-library';
import tracking from '@/utils/tracking-scripts';
import type { PermissionsUser } from '@customerio/hydra-permissions/dist/validation';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { InlineStack, Stack, PageLoader, SideNavProvider } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import {
  handleExpiredSessionFallback,
  handleWorkspaceSessionMismatch,
  useExpiredSessionSnackbar,
  useWorkspaceMismatchSnackbar,
  PENDING_SESSION_SWITCH_KEY,
} from '@/features/account-switcher';
import { SettingsNavigation } from './components/settings-navigation';
import { TopNavigation } from './components/top-navigation';
import { SampleWorkspaceBorder } from './components/top-navigation/components/sample-workspace';
import { WorkspaceStripe } from './components/workspace-stripe';
import {
  sentrySetDataCenter,
  sentrySetUser,
  setSentryFullStorySessionUrl,
  setSentryMasqueradingContext,
} from '@/utils/sentry';
import { Navigate } from '@/utils/navigate';
import { getWorkspaceIdFromUrl } from '@/context/current-workspace';
import { AuthProvider } from '@/services/auth/context';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { accountPermissionsForRole } from '@customerio/hydra-permissions/roles';
import { Region } from '@/services/auth/types';
import { AssistantFooter, CopilotSessionProvider, CopilotSidebarProvider } from '@/features/copilot';
import { getStorageValue } from '@/hooks/browser/use-local-storage';
import { AiEnabledGuard } from '@/components/guards';
import { MasqueradeBanner } from '@/features/masquerade-banner';
import * as styles from './index.css';
import { useCacheInvalidation } from '@/hooks/use-cache-invalidation';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useReleaseNotification } from './use-release-notification';
import { ReleaseNotificationModal } from './components/release-notification-modal';
import { WorkspaceModeBanners } from './components/workspace-mode-banners';

let didIdentify = false;

function AuthenticatedContent() {
  useCacheInvalidation();
  useExpiredSessionSnackbar();
  useWorkspaceMismatchSnackbar();
  const { showModal: showReleaseModal, reload: reloadForRelease } = useReleaseNotification();

  const auth = useAuthQuery();
  const workspaces = useListWorkspaces();
  const currentWorkspace = useCurrentWorkspace();
  const appContainerFadedOut = useStore(appContainerFadedOutStore);
  const appContainerOutletRendered = useStore(appContainerOutletRenderedStore);
  const themeContext = useThemeContext();
  const location = useLocation();
  const { isSettingsPage, isAnySettingsPage } = useIsSettings();
  const { isSampleWorkspace } = useSampleWorkspace();
  const roleDefinitions = useRoleDefinitions();
  const activeApp = useActiveBundle();

  const currentWorkspaceIdRef = useRef(currentWorkspace?.id != null ? `${currentWorkspace.id}` : null);
  const shouldScheduleAuthRefresh = useRef(false);
  const refreshAuthCallbackId = useRef<string | null>(null);

  const isSideNavDefaultCollapsed = getStorageValue<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  const { track } = useAnalytics();
  const collapsedAtRef = useRef<number | null>(isSideNavDefaultCollapsed === true ? Date.now() : null);
  const sessionExpandsAfterCollapseRef = useRef(0);

  const onSideNavCollapseChange = useCallback(
    (isCollapsed: boolean) => {
      if (isCollapsed) {
        collapsedAtRef.current = Date.now();
        void track('leftNav', 'collapsed', {
          Trigger: 'button_click',
          'Session Nav Opens After Collapse': sessionExpandsAfterCollapseRef.current,
        });
      } else {
        sessionExpandsAfterCollapseRef.current += 1;
        const timeCollapsedMs = collapsedAtRef.current != null ? Date.now() - collapsedAtRef.current : 0;
        collapsedAtRef.current = null;
        void track('leftNav', 'expanded', {
          Trigger: 'button_click',
          'Time Collapsed Ms': timeCollapsedMs,
        });
      }
    },
    [track],
  );

  useEffect(() => {
    // Set the top nav height to 44px to make sure child apps
    // have the correct CSS variable values
    themeContext.setVariable(GlobalCSSVariable.TopNavHeight, '44px');
  }, [themeContext]);

  useMountEffect(() => {
    return subscribeToRefreshAuth(async (event) => {
      const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
      const currentWorkspaceId = currentWorkspaceIdRef.current != null ? `${currentWorkspaceIdRef.current}` : null;

      // If the workspace id in the URL is different from the current workspace id
      // from the useCurrentWorkspace hook,
      // schedule an auth.refetch once the URL matches the current workspace id.
      // This is necessary because the useAuth hook reads the workspace ID from the URL,
      // but the URL may not update immediately, causing the auth data to be stale.
      if (currentWorkspaceId != null && currentWorkspaceId !== workspaceIdFromUrl) {
        shouldScheduleAuthRefresh.current = true;
        refreshAuthCallbackId.current = event.detail.id;
      } else {
        try {
          await auth.refetch();
        } finally {
          announceCallbackComplete(event.detail.id);
          refreshAuthCallbackId.current = null;
        }
      }
    });
  });

  useEffect(() => {
    const currentWorkspaceId = currentWorkspace?.id != null ? `${currentWorkspace.id}` : null;
    currentWorkspaceIdRef.current = currentWorkspaceId;
  }, [currentWorkspace?.id]);

  useEffect(() => {
    const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);

    // If there is an auth refresh scheduled and the workspace IDs finally match,
    // call .refetch on the auth hook query.
    if (shouldScheduleAuthRefresh.current && currentWorkspaceIdRef.current === workspaceIdFromUrl) {
      shouldScheduleAuthRefresh.current = false;
      const callback = async () => {
        try {
          await auth.refetch();
        } finally {
          if (refreshAuthCallbackId.current != null) {
            announceCallbackComplete(refreshAuthCallbackId.current);
            refreshAuthCallbackId.current = null;
          }
        }
      };
      callback();
    }
  }, [location.pathname, currentWorkspace?.id]);

  const isRefreshingAuthSampleWorkspace = useRef(false);
  useEffect(() => {
    // Switching to the sample workspace doesn't trigger
    // `subscribeToRefreshAuth`. If the current user doesn't have a role
    // for the sample workspace, refresh the auth data.
    if (currentWorkspace?.id != null && isSampleWorkspace(currentWorkspace.id)) {
      const workspaceId = currentWorkspace.id;
      const hasRoleForSampleWorkspace = auth.data?.user?.roles?.some((r) => r.workspace_id === workspaceId) ?? false;

      if (!hasRoleForSampleWorkspace && !auth.isLoading && !isRefreshingAuthSampleWorkspace.current) {
        const callback = async () => {
          isRefreshingAuthSampleWorkspace.current = true;
          try {
            await auth.refetch();
          } finally {
            isRefreshingAuthSampleWorkspace.current = false;
          }
        };
        callback();
      }
    }
  }, [isSampleWorkspace, currentWorkspace?.id, auth.data?.user?.roles, auth.isLoading]);

  useMountEffect(() => {
    return subscribeToRefreshWorkspaces(async (event) => {
      try {
        await workspaces.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  });

  useEffect(() => {
    const showTopColor = !isSettingsPage && currentWorkspace != null && !isEmptyString(currentWorkspace.color);

    if (showTopColor && currentWorkspace != null) {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderColor, currentWorkspace.color);
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '6px');
    } else {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '0px');
    }
  }, [currentWorkspace, isSettingsPage]);

  // Additional Sentry setup once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === false || auth.data == null) {
      return;
    }

    const { account, user } = auth.data;

    if (account == null || user == null) {
      return;
    }

    sentrySetUser(account, user);
    sentrySetDataCenter(account.region === Region.EU ? 'eu' : 'us');
    setSentryFullStorySessionUrl();
    setSentryMasqueradingContext(account.is_masq);
  }, [auth.data, auth.isSuccess]);

  // inject tracking scripts once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === true && auth.data != null && !didIdentify) {
      didIdentify = true;
      const { user } = auth.data;

      // identify
      void analytics?.identify(`${user?.id}`, {
        id: `${user?.id}`,
        email: user?.email,
      });
    }
  }, [auth.data, auth.isSuccess]);

  useEffect(() => {
    void analytics?.page();
  }, [location]);

  // clean tracking scripts + analytics once the component is unmounted
  useMountEffect(() => {
    return () => {
      tracking.cleanup();
      void analytics?.reset();
    };
  });

  // Using useMemo to make sure the attempted url setting runs before rendering,
  // i.e. before the Navigate component is rendered.
  useMemo(() => {
    if (auth.isLoading === true) {
      return;
    }

    if (auth.data == null) {
      const attemptedUrl = window.location.href.replace(/\/$/, '');
      if (getAttemptedTransitionUrl() == null && attemptedUrl !== window.location.origin) {
        setAttemptedTransitionUrl(attemptedUrl);
      }
    } else {
      clearAttemptedTransitionUrl();
    }
  }, [auth.data, auth.isLoading]);

  if (auth.isLoading === true) {
    return <PageLoader />;
  }

  if (auth.data == null || auth.data?.account == null || auth.data?.user == null) {
    if (handleExpiredSessionFallback()) {
      return <PageLoader />;
    }
    if (handleWorkspaceSessionMismatch()) {
      return <PageLoader />;
    }
    return <Navigate to="/login" saveCurrentUrl={true} />;
  } else {
    // Make sure we clear the pending switch marker
    sessionStorage.removeItem(PENDING_SESSION_SWITCH_KEY);
  }

  if (workspaces.isLoading) {
    return <PageLoader />;
  }

  // Sample workspace check fixes this issue https://customerio.slack.com/archives/C3EPR6SGH/p1764344886993649
  // The exact cause hasn't been pinpointed yet, need to investigate, but this fixes the issue
  if (roleDefinitions == null && !isSampleWorkspace(currentWorkspace?.id)) {
    return <PageLoader />;
  }

  const { account, user } = auth.data;

  // Set up permissions context
  const workspaceUserRoles =
    user.roles != null
      ? user.roles.reduce(
          (accumulator, userRole) => {
            accumulator[userRole.workspace_id] = { id: userRole.role_id, name: userRole.role_name };
            return accumulator;
          },
          {} as Record<number, { id?: number; name: string }>,
        )
      : null;

  const permissionsUser: PermissionsUser = {
    account_permissions: user.account_permissions,
    workspace_roles: workspaceUserRoles,
    masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
  };

  const permissionsWorkspaceId =
    (activeApp === AppBundle.Journeys ? currentWorkspace?.cio_workspace_id : currentWorkspace?.cdp_workspace_id) ??
    null;

  const allRoles: Record<string, RoleDefinition> = {};
  for (const role of Object.values(roleDefinitions ?? {})) {
    // custom roles don't have account permissions, so we populate what we have from the user
    const accountPermissions: Set<AccountPermission> = isStandardRole(role.name)
      ? accountPermissionsForRole(role.name)
      : new Set(user.account_permissions ?? []);

    // role names are unique
    allRoles[role.name] = {
      ...role,
      accountPermissions,
    };
  }

  // If we're masqing but the current user doesn't have masq permissions set, or
  // the permissions are "none", redirect to settings page (which is accessible
  // to all users who can masq)
  if (account.is_masq === true && (user.noMasqAccess === true || user.masqLevelNotSet === true) && !isAnySettingsPage) {
    return <Navigate to={appendMasqParam('/settings')} />;
  }

  return (
    <PermissionsProvider user={permissionsUser} account={account} roles={allRoles} workspaceId={permissionsWorkspaceId}>
      <AuthProvider auth={{ account, user }}>
        <CopilotSidebarProvider>
          <CopilotSessionProvider>
            <VideoLibraryProvider>
              <SideNavProvider
                defaultIsCollapsed={isSideNavDefaultCollapsed}
                onCollapseChange={onSideNavCollapseChange}
              >
                <RecordPerformanceMetric performanceMetric={PerformanceMetric.Authenticated} />
                <WorkspaceStripe />
                <SkipToContent />
                <MasqueradeBanner />
                <>
                  <WorkspaceModeBanners />
                  <Stack flexGrow="1" minH="0">
                    <TopNavigation isCollapsible={!isAnySettingsPage} />
                    <InlineStack alignBlock="stretch" flexGrow="1" minH="0" w="full" h="full">
                      <MainNavigationWrapper isCollapsible={!isAnySettingsPage}>
                        {!isAnySettingsPage ? <MainNavigation /> : null}
                        {isAnySettingsPage ? <SettingsNavigation /> : null}
                      </MainNavigationWrapper>
                      <div style={{ display: 'flex', minHeight: 0, minWidth: 0, flex: 1 }}>
                        <div className={clsx(styles.layoutContent, styles.layoutContentLayoutRefresh)}>
                          <div
                            id="embedded-app-container"
                            className={clsx(styles.layoutContentInner, styles.layoutContentInnerLayoutRefresh)}
                            style={{
                              opacity: appContainerFadedOut ? 0 : 1,
                            }}
                          >
                            {appContainerOutletRendered ? (
                              <AppErrorBoundary resetKeys={[location.pathname]}>
                                <Suspense fallback={<PageLoader />}>
                                  <Outlet />
                                </Suspense>
                              </AppErrorBoundary>
                            ) : null}
                          </div>
                          <AiEnabledGuard>
                            <AssistantFooter />
                          </AiEnabledGuard>
                        </div>
                        <SidebarRoot />
                        <HelpSidebar />
                      </div>
                    </InlineStack>
                  </Stack>
                </>
                <SampleWorkspaceBorder />
                <ReleaseNotificationModal isOpen={showReleaseModal} onReload={reloadForRelease} />
              </SideNavProvider>
              <VideoLibrary />
            </VideoLibraryProvider>
          </CopilotSessionProvider>
        </CopilotSidebarProvider>
      </AuthProvider>
    </PermissionsProvider>
  );
}

function PermissionsProviderWrapper() {
  return (
    <PerformanceProvider>
      <AuthenticatedContent />
    </PerformanceProvider>
  );
}

export default function Authenticated() {
  return (
    <CurrentWorkspaceProvider>
      <PermissionsProviderWrapper />
    </CurrentWorkspaceProvider>
  );
}
